import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, GitCompare, ShieldCheck, Store, MapPin, Calendar, Gauge, Fuel, Cog, Users,
  Palette, DoorOpen, Zap, Phone, Mail, MessageSquare, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Star, Check, CheckCircle2, ListChecks, Clock, Sparkles, FileText, Tag, TrendingUp, BadgeCheck, ArrowRight, Printer, Calculator,
} from "lucide-react";
import { api, mediaUrl, thumbUrl, getToken } from "@/lib/api";
import { crm } from "@/lib/crm";
import { useSuspended } from "@/lib/useSuspended";
import { formatPrice, formatPriceAsk, formatKm, cleanTitle } from "@/lib/format";
import { useMakes, logoFor } from "@/lib/useMakes";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";
import { useCompare } from "@/context/CompareContext";
import MarketComparison from "@/components/MarketComparison";
import InsurancePartnerButton, { useInsurancePartners } from "@/components/InsurancePartnerButton";
import RehabPanel from "@/components/RehabPanel";
import DnaSeal from "@/components/DnaSeal";
import EVSpecs from "@/components/EVSpecs";
import EVChargeSimulator from "@/components/EVChargeSimulator";
import EVChargingMap from "@/components/EVChargingMap";
import BatteryPassport from "@/components/BatteryPassport";
import CarCard from "@/components/CarCard";
import SeoInternalLinks from "@/components/SeoInternalLinks";
import Seo from "@/components/Seo";
import { buildListingSeo } from "@/lib/seoListing.mjs";
import SmartImg from "@/components/SmartImg";
import Lightbox from "@/components/Lightbox";
import { parseSpecBlocks, specIcon } from "@/lib/categorySpecs";
import { generateVehicleSheet } from "@/lib/vehicleSheet";
import { useRegion } from "@/context/RegionContext";
import { DEMO_BY_ID } from "@/data/demoListings";
import SocialPoster from "@/components/SocialPoster";
import ShareMenu from "@/components/ShareMenu";
import { applyOverride, displaySeller } from "@/lib/carMeta";
import { translateEquipment, translateEquipCategory } from "@/lib/equipmentI18n";
import { Maximize2, AlertTriangle, Receipt, ExternalLink, Pencil, Trash2, Landmark, Layers, Plane, AlarmClock } from "lucide-react";
import ShareButtons from "@/components/ShareButtons";
import { LicenseInfo } from "@/components/DrivingLicense";
import { GradeAssuranceBadge, PriceJustificationTag } from "@/components/CloudIntelligence";
import { extractId } from "@/lib/slug";
import { carSlug } from "@/lib/slug";
import { trackListingView, trackContactDealer, sendAttribution } from "@/lib/tracking";
import CarDamageSchema, { normalizeZones, SEVERITY_COLORS } from "@/components/CarDamageSchema";
import ListingEditModal from "@/components/ListingEditModal";
import FlyerStudioDialog from "@/components/FlyerStudioDialog";
import TaxCalculator from "@/components/TaxCalculator";
import FinancingSimulatorDialog from "@/components/FinancingSimulatorDialog";
import { computeCredit } from "@/lib/financing";
import { isTaxReady } from "@/lib/taxBelgium";
import { fuelLabel } from "@/data/fuels";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Eye } from "lucide-react";

// ── Live Urgence : « X personnes consultent ce véhicule » ──────────────────
// Chiffre lissé & déterministe par véhicule : jamais < 2, plafond 9–15 selon la
// popularité, dérive douce ±1 toutes les ~45 s pour simuler une activité réelle.
function LiveViewers({ carId, popularity = 0 }) {
  const seed = React.useMemo(() => {
    let h = 0;
    for (let i = 0; i < String(carId || "").length; i++) h = (h * 31 + String(carId).charCodeAt(i)) >>> 0;
    return h;
  }, [carId]);
  const cap = React.useMemo(() => (popularity > 40 ? 15 : popularity > 12 ? 12 : 9), [popularity]);
  const base = React.useMemo(() => 2 + (seed % Math.max(1, cap - 2)), [seed, cap]);
  const [n, setN] = React.useState(base);
  React.useEffect(() => { setN(base); }, [base]);
  React.useEffect(() => {
    const id = setInterval(() => {
      setN((prev) => {
        const step = ((seed + Date.now() / 45000) | 0) % 2 === 0 ? 1 : -1;
        return Math.min(cap, Math.max(2, prev + step));
      });
    }, 45000);
    return () => clearInterval(id);
  }, [seed, cap]);
  return (
    <div data-testid="live-viewers" className="live-urgence mt-2 inline-flex items-center gap-2 rounded-full border border-[#C9D2DC]/35 bg-[#0F2540]/70 px-3 py-1.5 backdrop-blur">
      <span className="live-urgence-dot" aria-hidden />
      <Eye size={13} className="text-[#C9D2DC]" />
      <span className="text-xs font-semibold text-[#DDE7F2]">
        <span data-testid="live-viewers-count" className="live-urgence-num font-display font-extrabold text-white">{n}</span> personnes consultent ce véhicule
      </span>
    </div>
  );
}


// Pactly Connect — appel tracé sans friction. Le numéro réel du vendeur n'est
// JAMAIS exposé : on affiche uniquement le numéro Pactly (ou un message clair si
// la mise en relation n'est pas encore active). Aucune connexion requise.
const PactlyCallButton = ({ carId, lang = "fr", sellerPhone }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [mode, setMode] = useState(null);      // null | "cb"
  const [cbPhone, setCbPhone] = useState("");
  const [cbState, setCbState] = useState(null); // {state} | {error}
  const T = {
    fr: { call: "Appeler via Pactly", connecting: "Connexion…", dialing: "Votre téléphone compose l'appel… Appuyez sur la touche d'appel.",
      redial: "Relancer l'appel", manual: "Composer manuellement", callback: "Être rappelé", num: "Composez ce numéro", then: "puis le code",
      cbTitle: "Recevez l'appel", cbPhone: "Votre numéro de téléphone", cbBtn: "Me faire appeler",
      cbSent: "Votre téléphone va sonner. Décrochez : nous vous mettons en relation avec le vendeur.",
      legal: "Votre numéro est transmis au vendeur pour qu'il puisse vous rappeler.", ringing: "Appel en cours…", noanswer: "Pas de réponse, réessayer", err: "Erreur, réessayez." },
    nl: { call: "Bellen via Pactly", connecting: "Verbinden…", dialing: "Uw telefoon belt… Druk op de belknop.",
      redial: "Opnieuw bellen", manual: "Handmatig bellen", callback: "Laat u terugbellen", num: "Bel dit nummer", then: "dan de code",
      cbTitle: "Word teruggebeld", cbPhone: "Uw telefoonnummer", cbBtn: "Bel mij",
      cbSent: "Uw telefoon gaat rinkelen. Neem op: we verbinden u met de verkoper.",
      legal: "Uw nummer wordt aan de verkoper doorgegeven zodat hij u kan terugbellen.", ringing: "Oproep bezig…", noanswer: "Geen antwoord, opnieuw", err: "Fout, probeer opnieuw." },
    en: { call: "Call via Pactly", connecting: "Connecting…", dialing: "Your phone is dialing… Press the call button.",
      redial: "Restart the call", manual: "Dial manually", callback: "Get a call back", num: "Dial this number", then: "then the code",
      cbTitle: "Receive the call", cbPhone: "Your phone number", cbBtn: "Call me",
      cbSent: "Your phone will ring. Pick up: we connect you to the seller.",
      legal: "Your number is shared with the seller so they can call you back.", ringing: "Call in progress…", noanswer: "No answer, retry", err: "Error, try again." },
  }[lang] || {};
  const isPhone = typeof window !== "undefined" && (
    /iPhone|iPod|Android.*Mobile/i.test(navigator.userAgent || "") ||
    (window.matchMedia && window.matchMedia("(pointer: coarse)").matches && !/iPad|Tablet/i.test(navigator.userAgent || "")));

  const start = async () => {
    setLoading(true);
    try {
      const d = await crm("/connect/call", { method: "POST", body: { car_id: carId, lang } });
      setState(d || {});
      if (isPhone && d && d.dial_link) { window.location.href = d.dial_link; }   // UN SEUL GESTE
      else if (!isPhone) { setMode("cb"); }
    } catch (e) {
      setState({ mode: "direct", seller_phone: sellerPhone });   // B5 retombée directe
    }
    setLoading(false);
  };

  const sendCallback = async () => {
    setCbState(null);
    try {
      const r = await crm("/connect/callback", { method: "POST", body: { car_id: carId, phone: cbPhone, lang } });
      if (r.mode === "direct") { setState({ mode: "direct", seller_phone: r.seller_phone }); return; }
      setCbState({ state: "queued", id: r.call_id });
      const t0 = Date.now();
      const poll = async () => {
        if (Date.now() - t0 > 120000) return;
        try { const s = await crm(`/connect/callback/${r.call_id}/status`); setCbState({ state: s.state, id: r.call_id });
          if (!["completed", "no-answer", "busy", "failed"].includes(s.state)) setTimeout(poll, 3000); } catch {}
      };
      setTimeout(poll, 3000);
    } catch (e) { setCbState({ error: e.message || T.err }); }
  };

  // B5 : retombée appel direct (ancien bouton tel: du vendeur)
  if (state && state.mode === "direct" && state.seller_phone) {
    return (
      <a href={`tel:${state.seller_phone}`} data-testid="pactly-direct-fallback" className="flex w-full items-center justify-center gap-2 rounded-xl gradient-brand px-4 py-3 text-sm font-bold text-white">
        <Phone size={16} /> {state.seller_phone}
      </a>
    );
  }

  if (state && state.twilio_enabled && isPhone) {
    return (
      <div data-testid="pactly-connect-result" className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground">
        <p className="font-semibold">{T.dialing}</p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {state.dial_link && <a href={state.dial_link} data-testid="pactly-redial" className="rounded-lg gradient-brand px-3 py-1.5 text-xs font-bold text-white">{T.redial}</a>}
          <button onClick={() => setReveal((v) => !v)} data-testid="pactly-manual" className="text-xs font-semibold text-muted-foreground underline">{T.manual}</button>
          <button onClick={() => setMode("cb")} data-testid="pactly-callback-link" className="text-xs font-semibold text-muted-foreground underline">{T.callback}</button>
        </div>
        {reveal && <div className="mt-2 text-xs text-muted-foreground">{T.num} <span className="font-mono font-bold text-foreground">{state.pactly_number}</span> — {T.then} <span className="font-mono font-bold text-foreground">{state.code}</span></div>}
        {mode === "cb" && <CallbackPanel {...{ T, cbPhone, setCbPhone, sendCallback, cbState }} />}
      </div>
    );
  }

  if (mode === "cb" || (state && state.twilio_enabled && !isPhone)) {
    return (
      <div data-testid="pactly-connect-result" className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground">
        <CallbackPanel {...{ T, cbPhone, setCbPhone, sendCallback, cbState }} />
        <button onClick={() => setReveal((v) => !v)} className="mt-2 text-xs font-semibold text-muted-foreground underline">{T.manual}</button>
        {reveal && state && <div className="mt-1 text-xs text-muted-foreground">{T.num} <span className="font-mono font-bold text-foreground">{state.pactly_number}</span> — {T.then} <span className="font-mono font-bold text-foreground">{state.code}</span></div>}
      </div>
    );
  }

  if (state && !state.twilio_enabled) {
    if (sellerPhone) return (
      <a href={`tel:${sellerPhone}`} data-testid="pactly-direct-fallback" className="flex w-full items-center justify-center gap-2 rounded-xl gradient-brand px-4 py-3 text-sm font-bold text-white"><Phone size={16} /> {sellerPhone}</a>);
    return <div data-testid="pactly-connect-result" className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">{state.message}</div>;
  }

  return (
    <div className="w-full">
      <Button data-testid="pactly-connect-btn" disabled={loading} onClick={start} className="w-full gradient-brand text-white">
        <Phone size={16} className="mr-2" /> {loading ? T.connecting : T.call}
      </Button>
      <p data-testid="pactly-legal-line" className="mt-1.5 text-xs text-muted-foreground">{T.legal}</p>
    </div>
  );
};

const CallbackPanel = ({ T, cbPhone, setCbPhone, sendCallback, cbState }) => (
  <div data-testid="pactly-callback-panel">
    <p className="font-semibold">{T.cbTitle}</p>
    <input data-testid="pactly-callback-phone" type="tel" autoComplete="tel" value={cbPhone} onChange={(e) => setCbPhone(e.target.value)}
      placeholder="+32 470 12 34 56" className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground outline-none focus:border-foreground/40" />
    <button data-testid="pactly-callback-send" onClick={sendCallback} className="mt-2 w-full rounded-lg gradient-brand px-3 py-2 text-sm font-bold text-white">{T.cbBtn}</button>
    <p className="mt-1.5 text-xs text-muted-foreground">{T.legal}</p>
    {cbState && <p data-testid="pactly-callback-state" className="mt-2 text-xs font-semibold text-foreground">
      {cbState.error ? cbState.error : cbState.state === "no-answer" ? T.noanswer : ["ringing", "in-progress"].includes(cbState.state) ? T.ringing : T.cbSent}</p>}
  </div>
);


export default function CarDetail() {
  const { id: rawId } = useParams();
  const id = extractId(rawId);
  const { t, lang } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isFav, toggle } = useFavorites();
  const { has, toggle: toggleCompare } = useCompare();
  const { effRegion } = useRegion();
  const makes = useMakes();
  const [descOpen, setDescOpen] = useState(false);

  const [carRaw, setCarRaw] = useState(null);
  const [deal, setDeal] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [sellerCars, setSellerCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [sellerAvatar, setSellerAvatar] = useState(null);
  const [sellerName, setSellerName] = useState(null);
  const [sellerIsPlatform, setSellerIsPlatform] = useState(false);
  const [sellerPhone, setSellerPhone] = useState(null);
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [ghostContact, setGhostContact] = useState(null);
  const [carMeta, setCarMeta] = useState(null);
  const [gaCert, setGaCert] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [dupAlts, setDupAlts] = useState([]);
  const [financing, setFinancing] = useState(null);
  const [financingOpen, setFinancingOpen] = useState(false);
  const [autoDesc, setAutoDesc] = useState(null);
  const insurancePartners = useInsurancePartners(carRaw?.id, carRaw?.vehicle_type);

  useEffect(() => {
    setLoading(true); setIdx(0); setSellerAvatar(null); setSellerName(null); setSellerIsPlatform(false); setSellerPhone(null); setPhoneRevealed(false); setCarMeta(null);
    const applyCar = (d) => {
      setCarRaw(d);
      const vehicle = `${d.make} ${d.model}${d.year ? ` (${d.year})` : ""}`;
      const link = `${window.location.origin}/car/${id}`;
      setMsg(t("d.prefillMessage", { vehicle, link }));
      if (d.seller_id) crm(`/profile/meta?seller_id=${encodeURIComponent(d.seller_id)}`).then((m) => { setSellerAvatar(m.avatar || null); setSellerName(m.display_name || null); setSellerIsPlatform(!!m.is_platform); setSellerPhone(m.phone || null); }).catch(() => {});
      crm(`/car-meta/${id}`).then(setCarMeta).catch(() => {});
      // Count a unique view per browser session for this listing.
      try {
        const seen = JSON.parse(sessionStorage.getItem("pactly_viewed") || "[]");
        if (!seen.includes(id)) {
          crm("/metrics/view", { method: "POST", body: { car_id: String(id), seller_id: d.seller_id || null } }).catch(() => {});
          sessionStorage.setItem("pactly_viewed", JSON.stringify([...seen, id].slice(-200)));
        }
      } catch {}
    };
    if (String(id).startsWith("demo-") && DEMO_BY_ID[id]) {
      applyCar(DEMO_BY_ID[id]);
      setLoading(false); setDeal(null); setSimilar([]); window.scrollTo(0, 0);
      return;
    }
    // LOCAL-FIRST: read the car from our backend mirror (instant); fall back to
    // the external API, then to our stored snapshot for purged imports.
    crm(`/cars/${id}`)
      .then((d) => applyCar(d))
      .catch(() =>
        api(`/cars/${id}`, { auth: false })
          .then((d) => applyCar(d))
          .catch(() => crm(`/imported/cars/${id}`).then((d) => applyCar(d)).catch(() => setCarRaw(null)))
      )
      .finally(() => setLoading(false));
    api(`/cars/${id}/deal-score?lang=${lang}`).then(setDeal).catch(() => setDeal(null));
    crm(`/cars/${id}/similar`).then((d) => setSimilar((d || []).slice(0, 4))).catch(() => setSimilar([]));
    window.scrollTo(0, 0);
  }, [id, lang]);

  // Localized description: translate the free-text description into the viewer's
  // language on-demand (cached server-side in desc_i18n) when no localized
  // version exists and the source language differs from the UI language.
  useEffect(() => {
    setAutoDesc(null);
    if (!carRaw) return;
    const merged = applyOverride(carRaw, carMeta);
    const desc = merged && merged.description;
    const already = carMeta && carMeta.desc_i18n && carMeta.desc_i18n[lang];
    if (!desc || String(desc).trim().length < 3 || already) return;
    const cc = String((merged && merged.country_code) || "").toUpperCase();
    const srcLang = { DE: "de", NL: "nl", FR: "fr", BE: "fr", ES: "es", IT: "it", PT: "pt" }[cc] || "fr";
    if (lang === srcLang) return; // already in the viewer's language
    let alive = true;
    crm(`/cars/${id}/description`, { method: "POST", body: { text: String(desc).slice(0, 4000), lang } })
      .then((r) => { if (alive && r && r.description) setAutoDesc(r.description); })
      .catch(() => {});
    return () => { alive = false; };
  }, [id, lang, carRaw, carMeta]);

  // ADMIN: same physical car merged from other platforms (hidden from the public
  // feed by the mirror de-duplication) — shown to staff as "also on ...".
  const _isAdmin = !!(user && user.is_admin);
  useEffect(() => {
    setDupAlts([]);
    if (!_isAdmin || !id) return;
    crm(`/cars/${id}/duplicates`).then((d) => setDupAlts(d?.duplicates || [])).catch(() => setDupAlts([]));
  }, [id, _isAdmin]);

  const [flyerOpen, setFlyerOpen] = useState(false);
  const [sellerLogo, setSellerLogo] = useState(null);

  const car = applyOverride(carRaw, carMeta);
  const suspendedIds = useSuspended();
  // Volet C : si la fiche a été ouverte via l'ANCIENNE URL (id/slug source), on
  // remplace immédiatement l'adresse par l'URL PUBLIQUE opaque (redirection client
  // `replace`) — l'adresse, le partage et data-seo-path/canonical deviennent propres.
  useEffect(() => {
    if (!carRaw?.id) return;
    const want = `/car/${carSlug(carRaw)}`;
    if (window.location.pathname !== want) navigate(want, { replace: true });
  }, [carRaw?.id]); // eslint-disable-line
  useEffect(() => { if (carRaw?.id) trackListingView(applyOverride(carRaw, carMeta)); }, [carRaw?.id]); // eslint-disable-line
  const dealer = displaySeller(carMeta);
  const financeFrom = (financing && car?.price)
    ? (() => {
        const down = Number(car.price) * (Number(financing.min_down_pct) || 0) / 100;
        const months = Math.max(...(financing.durations || [60]));
        const m = computeCredit({ price: Number(car.price), down, taeg: financing.taeg, months }).monthly;
        return m > 0 ? Math.round(m) : null;
      })()
    : null;
  const isNewVehicle = !!(car?.new_vehicle || car?.condition === "Neuf") && Number(car?.mileage || 0) === 0;
  const nvListPrice = Number(car?.list_price) || 0;
  const nvDiscount = isNewVehicle && nvListPrice > Number(car?.price || 0) ? Math.round((nvListPrice - car.price) / nvListPrice * 100) : 0;
  const nvLease = car?.lease_promo && car.lease_promo.monthly ? car.lease_promo : null;
  const sold = !!(car && (car.status === "sold" || car.sold === true));
  const soldUntil = car?.sold_until;
  const soldDaysLeft = soldUntil ? Math.max(0, Math.ceil((new Date(soldUntil) - Date.now()) / 864e5)) : null;
  const fmtSoldDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-BE", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; } };
  const localizedDesc = (carMeta && carMeta.desc_i18n && carMeta.desc_i18n[lang]) || autoDesc || (car && car.description);
  const specGroups = parseSpecBlocks(car && car.description).groups;
  const cleanDesc = parseSpecBlocks(localizedDesc).clean;
  const isOwner = !!(user && carRaw && carRaw.seller_id && [user.user_id, user.id].filter(Boolean).includes(carRaw.seller_id));
  const isAdmin = !!(user && user.is_admin);
  const isPro = !!(user && user.account_type === "pro");
  const canManage = isOwner || isAdmin;
  const deleteListing = async () => {
    if (!window.confirm("Supprimer définitivement cette annonce ?")) return;
    try {
      if (isAdmin) await crm(`/admin/listings/${id}`, { method: "DELETE", timeout: 30000 });
      else await api(`/cars/${id}`, { method: "DELETE" });
      toast.success("Annonce supprimée");
      navigate("/search");
    } catch (e) { toast.error(e.message || "Suppression impossible"); }
  };
  const refreshListing = () => {
    crm(`/car-meta/${id}`).then(setCarMeta).catch(() => {});
    api(`/cars/${id}`, { auth: false }).then(setCarRaw).catch(() => {});
  };

  // Load a few other listings from the same professional seller (showroom preview).
  const sellerKey = dealer?.dealer_id || (carRaw && carRaw.seller_id);
  useEffect(() => {
    setFinancing(null); setFinancingOpen(false);
    if (!sellerKey) return;
    crm(`/sellers/${encodeURIComponent(sellerKey)}/financing`)
      .then((d) => setFinancing(d?.available ? d : null)).catch(() => setFinancing(null));
    // Co-branding: fetch the dealer's uploaded showroom logo so the social
    // poster / flyer auto-fill even when display_seller has no logo.
    setSellerLogo(null);
    crm(`/seller-override/${encodeURIComponent(sellerKey)}`)
      .then((d) => setSellerLogo((d || {}).logo || null)).catch(() => {});
  }, [sellerKey]);
  useEffect(() => {
    if (!sellerKey) { setSellerCars([]); return; }
    let alive = true;
    (async () => {
      let cars = [];
      try {
        const r = await crm(`/sellers/${sellerKey}/imported-cars`);
        cars = r.cars || [];
      } catch { /* ignore */ }
      if (!cars.length) {
        try { cars = (await api(`/sellers/${sellerKey}/cars`, { auth: false })) || []; } catch { /* ignore */ }
      }
      if (alive) setSellerCars(cars.filter((c) => c && c.id !== id).slice(0, 3));
    })();
    return () => { alive = false; };
  }, [sellerKey, id]);

  useEffect(() => {
    const vin = String(carRaw?.vin || carRaw?.chassis || carRaw?.chassis_number || carMeta?.carpass_vin || carMeta?.vin || "").trim();
    if (!vin) { setGaCert(null); return; }
    let alive = true;
    crm(`/grade-assurance/for-vin/${vin}`).then((d) => { if (alive) setGaCert(d?.certified ? d : null); }).catch(() => {});
    return () => { alive = false; };
  }, [carRaw, carMeta]);

  if (loading) return (
    <div className="mx-auto max-w-[1400px] px-4 py-8 lg:px-8" data-testid="cardetail-skeleton">
      <div className="mb-5 h-4 w-64 animate-pulse rounded bg-white/10" />
      <div className="grid grid-cols-1 gap-8 min-w-0 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div>
          <div className="aspect-[16/10] w-full animate-pulse rounded-2xl bg-white/5" />
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-white/5" />)}
          </div>
          <div className="mt-6 h-44 animate-pulse rounded-2xl bg-white/5" />
          <div className="mt-6 h-28 animate-pulse rounded-2xl bg-white/5" />
        </div>
        <div className="space-y-6">
          <div className="h-72 animate-pulse rounded-2xl bg-white/5" />
          <div className="h-40 animate-pulse rounded-2xl bg-white/5" />
        </div>
      </div>
    </div>
  );
  if (!car) return (
    <div className="mx-auto max-w-[1400px] px-4 py-24 text-center lg:px-8">
      <p className="text-lg text-muted-foreground">{t("d.notFound")}</p>
      <Link to="/search"><Button className="mt-6 gradient-brand text-white">{t("nav.buy")}</Button></Link>
    </div>
  );

  if (car.seller_id && suspendedIds.has(String(car.seller_id))) return (
    <div className="mx-auto max-w-[1400px] px-4 py-24 text-center lg:px-8" data-testid="car-unavailable">
      <p className="font-display text-xl font-bold text-white">Annonce indisponible</p>
      <p className="mt-2 text-muted-foreground">Cette annonce n'est plus disponible sur Pactly Car.</p>
      <Link to="/search"><Button className="mt-6 gradient-brand text-white">{t("nav.buy")}</Button></Link>
    </div>
  );

  const images = (car.images || []).map(mediaUrl).filter(Boolean);
  const logo = logoFor(makes, car.make);
  const fav = isFav(car.id);
  const inCompare = has(car.id);
  // Year can be missing on imported listings: never print "null" in titles.
  const title = [cleanTitle(car.make, car.model), car.year].filter(Boolean).join(" ");
  const priceValidUntil = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  // Per-listing SEO (title, description, canonical, Vehicle JSON-LD) — same
  // helper as the build-time prerender (scripts/prerender-seo.mjs).
  const seo = buildListingSeo({ ...car, images }, { sold, priceValidUntil });

  const specs = [
    { icon: Calendar, label: t("d.year"), value: car.year },
    { icon: Gauge, label: t("d.mileage"), value: formatKm(car.mileage) },
    { icon: Fuel, label: t("d.fuel"), value: fuelLabel(car.fuel, lang) },
    { icon: Cog, label: t("d.transmission"), value: car.transmission },
    { icon: Zap, label: t("d.power"), value: car.power ? `${car.power} ch` : "—" },
    { icon: Palette, label: t("d.color"), value: car.color },
    { icon: DoorOpen, label: t("d.doors"), value: car.doors },
    { icon: Users, label: t("d.seats"), value: car.seats },
    { icon: Calendar, label: t("d.firstReg"), value: car.first_registration },
    { icon: Users, label: t("d.owners"), value: car.owners },
  ].filter((s) => s.value != null && s.value !== "");

  const onFav = async () => { const r = await toggle(car.id); if (r?.needAuth) toast.error(t("fav.loginNeeded")); };

  const trackContact = (kind) => {
    if (!car) return;
    trackContactDealer(kind, car);
    crm("/metrics/contact", { method: "POST", body: { car_id: String(car.id), seller_id: car.seller_id || null, kind } }).catch(() => {});
  };

  const sendMessage = async () => {
    if (!user) { navigate("/login"); return; }
    setSending(true);
    try {
      trackContact("message");
      sendAttribution("lead", { type: "lead" });
      const conv = await crm("/conversations", { method: "POST", body: { car_id: String(car.id) } });
      if (msg.trim()) await crm(`/conversations/${conv.id}/messages`, { method: "POST", body: { text: msg.trim() } });
      toast.success(t("common.send") + " ✓");
      setContactOpen(false);
      navigate(`/messages/${conv.id}`);
    } catch (e) { toast.error(e.message || "Erreur"); }
    finally { setSending(false); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-10 lg:px-8">
      <Seo title={seo.title.replace(/ \| Pactly Car$/, "")}
           fullTitle={seo.title}
           description={seo.description}
           url={seo.canonical}
           image={images[0]} type="product"
           jsonLd={seo.jsonLd} />

      <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-white">Pactly Car</Link> /
        <Link to="/search" className="hover:text-white">{t("nav.buy")}</Link> /
        <span className="min-w-0 break-words text-white/80">{title}</span>
      </nav>

      <div className="grid grid-cols-1 gap-8 min-w-0 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        {/* LEFT: gallery + description + specs */}
        <div className="min-w-0">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="overflow-hidden rounded-2xl card-surface">
            <div className="group relative aspect-[16/10] cursor-zoom-in bg-white/5" onClick={() => setLightboxOpen(true)} data-testid="gallery-main">
              <SmartImg src={images[idx]} alt={title} className="h-full w-full" eager width={1600} q={90} vtype={car.vehicle_type} />
              <span className="pointer-events-none absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-xs font-semibold text-white opacity-0 backdrop-blur-sm transition group-hover:opacity-100">
                <Maximize2 size={13} /> {t("common.viewAll")}
              </span>
              {sold && (
                <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 on-dark bg-emerald-950/45 backdrop-grayscale">
                  <span className="rotate-[-9deg] select-none rounded-xl border-4 border-emerald-400 bg-emerald-500/15 px-8 py-2.5 font-display text-3xl font-black uppercase tracking-[0.2em] text-emerald-300 shadow-[0_4px_30px_rgba(16,185,129,0.6)] sm:text-4xl">Vendu</span>
                  {soldDaysLeft != null && (
                    <span className="rounded-full bg-black/60 px-3 py-1.5 text-xs font-bold text-emerald-100 backdrop-blur">
                      {soldDaysLeft > 0 ? `Annonce retirée dans ${soldDaysLeft} jour${soldDaysLeft > 1 ? "s" : ""}` : "Annonce retirée aujourd'hui"}
                    </span>
                  )}
                </div>
              )}
              {images.length > 1 && (
                <>
                  <NavBtn side="left" onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); }} />
                  <NavBtn side="right" onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length); }} />
                  <span className="absolute bottom-3 right-3 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white">{idx + 1}/{images.length}</span>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto p-3 no-scrollbar">
                {images.map((im, i) => (
                  <button key={i} data-testid={`thumb-${i}`} onClick={() => setIdx(i)}
                    className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 transition ${i === idx ? "border-primary" : "border-transparent opacity-60 hover:opacity-100"}`}>
                    <img src={thumbUrl(im, 200)} alt="" loading="lazy" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* key highlights */}
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[
              { icon: Tag, label: t("d.price"), value: formatPriceAsk(car.price), id: "price", accent: true },
              { icon: Calendar, label: t("d.year"), value: car.year || "—", id: "year" },
              { icon: Gauge, label: t("d.mileage"), value: formatKm(car.mileage), id: "mileage" },
              { icon: Fuel, label: t("d.fuel"), value: fuelLabel(car.fuel, lang), id: "fuel" },
              { icon: Zap, label: t("d.power"), value: car.power ? `${car.power} ch` : "—", id: "power" },
              (car.location || dealer?.location) && { icon: MapPin, label: t("d.location"), value: car.location || dealer?.location, id: "location" },
            ].filter(Boolean).map((h, i) => (
              <div key={i} data-testid={`highlight-${h.id}`} className={`flex items-center gap-2.5 rounded-xl card-surface px-3 py-3 ${h.accent ? "ring-1 ring-emerald-400/40" : ""}`}>
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white ${h.accent ? "bg-emerald-500" : "gradient-brand"}`}><h.icon size={16} /></span>
                <div className="min-w-0">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{h.label}</div>
                  <div className="truncate font-display text-sm font-bold text-white">{h.value}</div>
                </div>
              </div>
            ))}
          </div>

          {((carMeta && carMeta.export) || car.export) && (
            <div data-testid="detail-export-block" className="mt-3 flex items-center gap-3 rounded-xl border border-[#C9D2DC]/60 bg-gradient-to-r from-[#14243A] to-[#1E3A8A] px-4 py-3 shadow-sm">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-[#C9D2DC]/30">
                <Plane size={18} className="text-[#C9D2DC]" />
              </span>
              <div>
                <div className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-white">
                  <span className="rounded bg-[#C9D2DC]/20 px-1.5 py-0.5 text-[10px]">✈️ Export</span>
                </div>
                <p className="mt-0.5 text-sm font-medium text-[#DDE4EC]">
                  Véhicule disponible pour la vente internationale (Transit/Export)
                </p>
              </div>
            </div>
          )}

          {carMeta && carMeta.accidented && (
            <div data-testid="detail-accidented" className="mt-3 rounded-xl border border-amber-500/60 bg-amber-100/70 px-4 py-3">
              <div className="flex items-center gap-2.5 text-sm font-semibold text-amber-800">
                <AlertTriangle size={18} className="shrink-0" /> {t("detail.accidented")}
              </div>
              {carMeta.honesty_note && (
                <p data-testid="detail-honesty-note" className="mt-2 whitespace-pre-line text-sm font-medium text-amber-900/90">"{carMeta.honesty_note}"</p>
              )}
              {Array.isArray(carMeta.damage_zones) && carMeta.damage_zones.length > 0 && (() => {
                const zs = normalizeZones(carMeta.damage_zones);
                const sevLabel = { light: t("damage.severity.light"), medium: t("damage.severity.medium"), severe: t("damage.severity.severe") };
                return (
                  <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3 dark:border-amber-400/30 dark:bg-amber-400/[0.06]" data-testid="detail-damage-zones">
                    <div className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300/80">{t("damage.schema.title")}</div>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                      <CarDamageSchema value={zs} readOnly />
                      <div className="min-w-0 flex-1 space-y-2">
                        {zs.map((z) => (
                          <div key={z.key} className="rounded-lg border border-amber-200/70 bg-white/60 p-2 dark:border-white/10 dark:bg-white/[0.03]">
                            <div className="flex items-center gap-2">
                              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: SEVERITY_COLORS[z.severity] }} />
                              <span className="flex-1 text-sm font-bold text-slate-800 dark:text-white">{t(`damage.zone.${z.key}`)}</span>
                              <span className="rounded-full px-2 py-0.5 text-[10px] font-extrabold text-white" style={{ background: SEVERITY_COLORS[z.severity] }}>{sevLabel[z.severity]}</span>
                            </div>
                            {z.photos && z.photos.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {z.photos.map((src, i) => (
                                  <img key={i} src={src} alt="" className="h-14 w-14 rounded-lg object-cover ring-1 ring-black/5" />
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
              {Array.isArray(carMeta.damage_photos) && carMeta.damage_photos.length > 0 && normalizeZones(carMeta.damage_zones).every((z) => !(z.photos || []).length) && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="detail-damage-photos">
                  {carMeta.damage_photos.map((src, i) => (
                    <a key={i} href={src} target="_blank" rel="noreferrer" className="h-20 w-20 overflow-hidden rounded-lg border border-amber-500/40">
                      <img src={src} alt={`Dégât ${i + 1}`} className="h-full w-full object-cover transition hover:scale-105" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {carMeta && carMeta.carpass_certified_km != null && car.mileage && (() => {
            const cert = Number(carMeta.carpass_certified_km);
            const entered = Number(car.mileage);
            const diff = entered - cert;
            const tol = Math.max(1000, cert * 0.03);
            const level = entered < cert - tol ? "error" : Math.abs(diff) <= tol ? "ok" : "warn";
            const cls = level === "ok" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
              : level === "warn" ? "border-amber-400/40 bg-amber-400/10 text-amber-400"
              : "border-rose-500/40 bg-rose-500/10 text-rose-400";
            const label = level === "ok" ? t("carpass.conf.match") : level === "error" ? t("carpass.conf.below") : t("carpass.conf.gap");
            return (
              <div data-testid="carpass-mileage-badge" className={`mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border px-4 py-3 text-sm font-semibold ${cls}`}>
                {level === "ok" ? <ShieldCheck size={16} /> : <Gauge size={16} />}
                {label}
                <span className="text-xs font-medium opacity-80">· {diff > 0 ? "+" : ""}{diff.toLocaleString("fr-BE")} km ({cert.toLocaleString("fr-BE")} km {t("carpass.conf.certified")})</span>
              </div>
            );
          })()}

          {(() => {
            const vin = String(car.vin || car.chassis || car.chassis_number || carMeta?.carpass_vin || carMeta?.vin || "").trim();
            const carpassUrl = carMeta?.carpass_url || (vin ? "https://www.car-pass.be" : null);
            if (!carpassUrl) return null;
            const generated = !carMeta?.carpass_url && !!vin;
            return (
              <a href={carpassUrl} target="_blank" rel="noreferrer" data-testid="carpass-link"
                title={generated && vin ? `Car-Pass · VIN ${vin}` : undefined}
                className="mt-3 inline-flex items-center gap-2.5 rounded-xl border border-emerald-500/40 bg-white px-4 py-2.5 shadow-sm transition hover:shadow-md hover:border-emerald-500/70">
                <img src="/carpass-logo.png" alt="Car-Pass" className="h-6 w-auto" />
                <span className="text-sm font-bold text-slate-700">{t("carpass.check")}</span>
                {generated && vin && <span className="font-mono text-[11px] text-slate-400">VIN {vin}</span>}
                <ExternalLink size={14} className="text-slate-400" />
              </a>
            );
          })()}

          {(() => {
            const carpassLinkShown = !!(carMeta?.carpass_url || car.vin || car.chassis || car.chassis_number || carMeta?.carpass_vin || carMeta?.vin);
            const hasCarpass = (car.options || []).some((o) => String(o).toLowerCase().includes("car-pass")) || /car-?pass/i.test(car.description || "");
            const hasVin = (car.options || []).some((o) => /données constructeur/i.test(String(o)));
            const items = [
              hasVin && { key: "vin", icon: ShieldCheck, color: "violet", title: t("vin.badge"), hint: t("vin.badgeHint") },
              (hasCarpass && !carpassLinkShown) && { key: "carpass", icon: ShieldCheck, color: "emerald", title: t("carpass.verified"), hint: t("carpass.hint") },
              car.seller_verified && { key: "seller", icon: BadgeCheck, color: "blue", title: t("trust.sellerVerified"), hint: t("trust.sellerVerifiedHint") },
              car.seller_merchant_verified && { key: "pro", icon: Store, color: "amber", title: t("trust.proVerified"), hint: t("trust.proVerifiedHint") },
            ].filter(Boolean);
            if (!items.length) return null;
            const C = {
              violet: "border-pactly-violet/40 bg-pactly-violet/10 text-pactly-violet",
              emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
              blue: "border-pactly-blue/40 bg-pactly-blue/10 text-pactly-blue",
              amber: "border-amber-400/40 bg-amber-400/10 text-amber-400",
            };
            return (
              <div data-testid="trust-block" className="mt-4 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-pactly-violet/[0.08] to-pactly-blue/[0.05] p-5">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg gradient-brand text-white"><ShieldCheck size={16} /></span>
                  <h3 className="font-display text-sm font-bold uppercase tracking-wide text-white/80">{t("trust.title")}</h3>
                </div>
                <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
                  {items.map((it) => (
                    <div key={it.key} data-testid={`trust-${it.key}`} className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 ${C[it.color]}`}>
                      <it.icon size={17} className="mt-0.5 shrink-0" />
                      <div>
                        <div className="text-[13px] font-bold leading-tight">{it.title}</div>
                        <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{it.hint}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* specs */}
          <div className="mt-6 rounded-2xl card-surface p-6">
            <h2 className="font-display text-xl font-bold text-white">{t("d.specs")}</h2>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {specs.map((s, i) => (
                <div key={i} className="min-w-0 rounded-xl bg-white/5 p-3">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><s.icon size={13} className="shrink-0" /> {s.label}</span>
                  <div className="mt-1 break-words font-display text-sm font-bold text-white">{s.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Pactly Electric — fiche technique EV (auto si véhicule électrique/hybride) */}
          <div className="mt-6"><EVSpecs car={car} /></div>
          <EVChargeSimulator car={car} />
          <EVChargingMap car={car} />
          <BatteryPassport car={car} />

          {/* category specs (boat / van / truck / moto …) — pretty grid with dedicated icons */}
          {specGroups.length > 0 && (
            <div className="mt-6 rounded-2xl card-surface p-6" data-testid="detail-cat-specs">
              {specGroups.map((g, gi) => (
                <div key={gi} className={gi > 0 ? "mt-6" : ""}>
                  <h2 className="font-display text-xl font-bold text-white">{g.title}</h2>
                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {g.items.map((it, i) => {
                      const Ic = specIcon(it.label || it.value);
                      return (
                        <div key={i} data-testid={`cat-spec-${gi}-${i}`} className="flex items-start gap-3 rounded-xl bg-white/5 p-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-pactly-violet/10 text-pactly-violet">
                            <Ic size={17} strokeWidth={1.9} />
                          </span>
                          <div className="min-w-0">
                            {it.label && <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{it.label}</div>}
                            <div className="break-words text-sm font-bold text-white">{it.value}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Caractéristiques poids-lourd — champs techniques enrichis */}
          {car.ts_specs && Object.keys(car.ts_specs).length > 0 && (() => {
            const s = car.ts_specs;
            const fmtKg = (v) => (v >= 1000 ? `${(v / 1000).toLocaleString("fr-FR")} t (${v.toLocaleString("fr-FR")} kg)` : `${v.toLocaleString("fr-FR")} kg`);
            const dims = [s.length_mm, s.width_mm, s.height_mm].filter(Boolean);
            const rows = [
              s.weight_total_kg && { label: "Poids total (PTAC)", value: fmtKg(s.weight_total_kg) },
              s.weight_empty_kg && { label: "Poids à vide", value: fmtKg(s.weight_empty_kg) },
              s.payload_kg && { label: "Charge utile", value: fmtKg(s.payload_kg) },
              dims.length === 3 && { label: "Dimensions (L×l×h)", value: `${(dims[0] / 1000).toFixed(2)} × ${(dims[1] / 1000).toFixed(2)} × ${(dims[2] / 1000).toFixed(2)} m` },
              s.axle_config && { label: "Configuration d'essieux", value: s.axle_config },
              s.suspension && { label: "Suspension", value: s.suspension },
              s.brakes && { label: "Freins", value: s.brakes },
              s.euro && { label: "Norme d'émission", value: s.euro },
              s.gearbox && { label: "Boîte de vitesses", value: s.gearbox },
              (s.power_hp || s.power_kw) && { label: "Puissance", value: `${s.power_hp ? s.power_hp + " ch" : ""}${s.power_hp && s.power_kw ? " · " : ""}${s.power_kw ? s.power_kw + " kW" : ""}` },
              s.cab && { label: "Cabine", value: s.cab },
              s.color && { label: "Couleur", value: s.color },
              s.first_registration && { label: "1re immatriculation", value: s.first_registration },
              s.listing_ref && { label: "Réf. annonce", value: s.listing_ref },
            ].filter(Boolean);
            return (
              <div className="mt-6 rounded-2xl card-surface p-6" data-testid="detail-truck-specs">
                <h2 className="flex items-center gap-2 font-display text-xl font-bold text-white">
                  <ShieldCheck size={20} className="text-[#8FB4E3]" /> Caractéristiques poids-lourd
                </h2>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {rows.map((r, i) => (
                    <div key={i} data-testid={`truck-spec-${i}`} className="flex items-start gap-3 rounded-xl bg-white/5 p-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1E5288]/15 text-[#8FB4E3]">
                        <Cog size={17} strokeWidth={1.9} />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{r.label}</div>
                        <div className="break-words text-sm font-bold text-white">{r.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}


          {/* driving-license requirement (dynamic, from category + PTAC) */}
          <LicenseInfo car={car} />

          {/* description — AutoScout-style 'Lire la suite' collapse + bold subtitles */}
          {cleanDesc && (() => {
            const descLong = cleanDesc.split("\n").length > 6 || cleanDesc.length > 360;
            const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
            const isSub = (ln) => {
              const tt = ln.trim();
              if (tt.length < 2 || tt.length > 42) return false;
              return /[:：]\s*$/.test(tt) || (tt === tt.toUpperCase() && /[A-ZÀ-Ÿ]/.test(tt) && tt.split(/\s+/).length <= 6);
            };
            const descHtml = cleanDesc.split("\n")
              .map((ln) => (isSub(ln) ? `<strong class="text-white font-bold">${esc(ln)}</strong>` : esc(ln)))
              .join("\n");
            return (
              <div className="mt-6 rounded-2xl card-surface p-6">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg gradient-brand text-white"><FileText size={16} /></span>
                  <h2 className="font-display text-xl font-bold text-white">{t("d.description")}</h2>
                </div>
                <p className={`mt-4 whitespace-pre-line break-words leading-relaxed text-white/80 ${descLong && !descOpen ? "line-clamp-6" : ""}`}
                  data-testid="detail-description" dangerouslySetInnerHTML={{ __html: descHtml }} />
                {descLong && (
                  <button data-testid="desc-toggle" onClick={() => setDescOpen((v) => !v)}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-primary transition-colors hover:text-primary/80">
                    {descOpen ? <>Voir moins <ChevronUp size={15} /></> : <>Lire la suite <ChevronDown size={15} /></>}
                  </button>
                )}
              </div>
            );
          })()}

          {/* TVA déductible — mise en avant pour les pros (véhicules importés) */}
          {!!(carMeta && carMeta.vat_deductible) && (() => {
            const net = carMeta.vat_net_price || (car.price ? Math.round(car.price / 1.21) : null);
            const vat = net && car.price ? Math.round(car.price - net) : null;
            return (
              <div data-testid="detail-vat-section" className="mt-6 overflow-hidden rounded-2xl border border-[#7FB2E8]/25 bg-gradient-to-br from-[#0F2540] to-[#14243A] p-6">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#1E5288] to-[#3b82f6] text-white"><Receipt size={16} /></span>
                  <h2 className="font-display text-xl font-bold text-white">TVA déductible</h2>
                  <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#7FB2E8]/15 px-2.5 py-1 text-xs font-bold text-[#D6E6FA]"><Check size={12} /> Récupérable</span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-white/70">
                  Ce véhicule est vendu <span className="font-semibold text-white">TVA déductible</span> : un professionnel assujetti peut récupérer la TVA. Idéal pour les sociétés et indépendants.
                </p>
                {net && (
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
                      <div className="text-[11px] font-medium text-white/45">Prix HTVA</div>
                      <div className="font-display text-lg font-extrabold text-white">{formatPrice(net)}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
                      <div className="text-[11px] font-medium text-white/45">TVA (21%)</div>
                      <div className="font-display text-lg font-extrabold text-[#AFD1F2]">{vat ? formatPrice(vat) : "—"}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
                      <div className="text-[11px] font-medium text-white/45">Prix TVAC</div>
                      <div className="font-display text-lg font-extrabold text-white">{formatPrice(car.price)}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* équipements & options — groupés par catégorie */}
          {(() => {
            const clean = (arr) => [...new Set((arr || [])
              .map((o) => String(o).trim())
              .filter((o) => o && !/^import[ée]$/i.test(o)))];
            const tr = (o) => translateEquipment(o, lang);
            const grouped = Array.isArray(car.options_grouped)
              ? car.options_grouped
                  .map((g) => ({ category: translateEquipCategory(g.category || "", lang) || g.category, items: [...new Set(clean(g.items).map(tr))] }))
                  .filter((g) => g.items.length)
              : [];
            const flat = [...new Set(clean(car.options).map(tr))];
            const total = grouped.length
              ? [...new Set(grouped.flatMap((g) => g.items))].length
              : flat.length;
            if (!total) return null;
            return (
              <div className="mt-6 rounded-2xl card-surface p-6" data-testid="detail-options">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg gradient-brand text-white"><ListChecks size={16} /></span>
                  <h2 className="font-display text-xl font-bold text-white">{t("d.options")}</h2>
                  <span className="ml-auto rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-bold text-emerald-400">{total}</span>
                </div>
                {grouped.length ? (
                  <div className="mt-5 space-y-6">
                    {grouped.map((g, gi) => (
                      <div key={g.category} data-testid={`option-group-${gi}`}>
                        <div className="mb-3 flex items-center gap-2">
                          <span className="h-1.5 w-1.5 rounded-full bg-pactly-blue" />
                          <h3 className="font-display text-sm font-bold uppercase tracking-wide text-white/70">{g.category}</h3>
                          <span className="text-xs font-semibold text-white/35">{g.items.length}</span>
                          <span className="ml-2 h-px flex-1 bg-white/8" />
                        </div>
                        <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                          {g.items.map((o, i) => (
                            <div key={o} data-testid={`option-${gi}-${i}`} className="flex items-start gap-2.5 border-b border-white/5 pb-2.5">
                              <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-400" strokeWidth={2.2} />
                              <span className="break-words text-sm font-medium leading-snug text-white/85">{o}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                    {flat.map((o, i) => (
                      <div key={o} data-testid={`option-${i}`} className="flex items-start gap-2.5 border-b border-white/5 pb-2.5">
                        <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-400" strokeWidth={2.2} />
                        <span className="break-words text-sm font-medium leading-snug text-white/85">{o}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* RIGHT: price / score / seller (sticky) */}
        <div className="min-w-0">
          {/* max-h + overflow sur grand écran : la colonne sticky dépasse la hauteur
              de l'écran (prix, score, contact, bloc vendeur, showroom). Sans cela, son
              bas — dont le bouton « Voir le numéro / Appeler » — reste figé SOUS la ligne
              de flottaison et devient inatteignable au scroll (bouton « mort »). Le scroll
              interne garantit que l'acheteur atteint TOUJOURS le contact vendeur. */}
          <div className="space-y-6 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:overflow-x-hidden lg:pr-1">
            {/* Accès vendeur instantané — contact + appel tout en haut de la colonne,
                sans scroll (bloc remonté depuis la carte vendeur plus bas). */}
            <div className="rounded-2xl card-surface p-5" data-testid="top-contact-card">
              <div className="mb-3 flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 font-display text-sm font-extrabold uppercase tracking-wide text-white">
                  <MessageSquare size={14} className="text-pactly-blue" /> {t("d.contactSeller")}
                </span>
                {car.seller_fast_responder && (
                  <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-400">
                    <Clock size={11} /> ~{car.seller_response_minutes || 30} min
                  </span>
                )}
              </div>
              {car.contact_mode === "pactly" ? (
                <div className="grid grid-cols-1 gap-3">
                  <Button variant="outline" onClick={() => setContactOpen(true)} className="w-full min-w-0 border-white/10 bg-white/5 text-white hover:bg-white/10">
                    <Mail size={16} className="mr-2 shrink-0" /> <span className="truncate">{t("d.sendMessage")}</span>
                  </Button>
                  {car.call_available && <PactlyCallButton carId={car.id} lang={lang} />}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Button variant="outline" onClick={() => setContactOpen(true)} className="border-white/10 bg-white/5 text-white hover:bg-white/10">
                      <Mail size={16} className="mr-2" /> {t("d.sendMessage")}
                    </Button>
                    {(() => {
                      if (car.ghost) {
                        if (ghostContact && !ghostContact.locked) {
                          if (ghostContact.phone) {
                            const clean = String(ghostContact.phone).replace(/[^\d+]/g, "");
                            return (
                              <Button asChild variant="outline" className="border-emerald-400/30 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20">
                                <a href={`tel:${clean}`} data-testid="ghost-call-btn" onClick={() => trackContact("ghost_phone")}><Phone size={16} className="mr-2" /> {ghostContact.phone}</a>
                              </Button>
                            );
                          }
                        }
                        return (
                          <Button variant="outline" data-testid="ghost-reveal-btn"
                            onClick={async () => {
                              if (!user) { navigate(`/login?redirect=${encodeURIComponent(`/car/${id}`)}`); return; }
                              try { const r = await crm(`/cars/${id}/contact`); setGhostContact(r); trackContact("ghost_unlock"); } catch { /* noop */ }
                            }}
                            className="border-emerald-400/30 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20"><Phone size={16} className="mr-2" /> {lang === "nl" ? "Contact bekijken" : lang === "en" ? "View contact" : "Voir le contact"}</Button>
                        );
                      }
                      const raw = car.seller_phone || sellerPhone;
                      const clean = raw ? String(raw).replace(/[^\d+]/g, "") : "";
                      if (!clean) return (<Button variant="outline" data-testid="detail-call-btn" onClick={() => setContactOpen(true)} className="border-white/10 bg-white/5 text-white hover:bg-white/10"><Phone size={16} className="mr-2" /> {t("d.call")}</Button>);
                      if (!phoneRevealed) return (<Button variant="outline" data-testid="detail-reveal-phone-btn" onClick={() => { setPhoneRevealed(true); trackContact("reveal_phone"); }} className="border-white/10 bg-white/5 text-white hover:bg-white/10"><Phone size={16} className="mr-2" /> {t("d.revealPhone")}</Button>);
                      return (
                        <Button asChild variant="outline" className="border-emerald-400/30 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20">
                          <a href={`tel:${clean}`} data-testid="detail-call-btn" onClick={() => trackContact("phone")}><Phone size={16} className="mr-2" /> {raw}</a>
                        </Button>
                      );
                    })()}
                  </div>
                  {car.ghost && !ghostContact && car.phone_masked && (
                    <div className="mt-2 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-muted-foreground" data-testid="ghost-masked-hint">
                      <Phone size={13} className="text-emerald-300" /><span className="font-mono tracking-wide text-white/80">{car.phone_masked}</span>
                      <span className="text-xs">— {user ? (lang === "nl" ? "klik om te tonen" : lang === "en" ? "click to reveal" : "cliquez pour afficher") : (lang === "nl" ? "gratis account vereist" : lang === "en" ? "free account required" : "compte gratuit requis")}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="rounded-2xl card-surface p-6">
              <div className="flex items-center gap-2">
                {logo && <span className="flex h-8 w-8 items-center justify-center rounded-md bg-white p-1"><img src={logo} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} /></span>}
                {car.featured && <span className="rounded-md gradient-brand px-2 py-0.5 text-[10px] font-extrabold uppercase text-white">{t("home.featured")}</span>}
                {car.urgent && !car.sold && <span data-testid="car-detail-urgent-badge" className="flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-extrabold uppercase text-white" style={{ backgroundColor: "#E11D48" }}><AlarmClock size={11} className="animate-pulse" /> Vente Urgente</span>}
                {car.ref && <span className="ml-auto text-xs font-semibold text-muted-foreground">{car.ref}</span>}
              </div>
              <h1 className="mt-3 break-words font-display text-2xl font-extrabold text-white lg:text-3xl">{cleanTitle(car.make, car.model)}</h1>
              {gaCert?.certified && (
                <div className="mt-2" data-testid="detail-ga-badge">
                  <Link to={`/verify/${gaCert.cert_id}`} style={{ textDecoration: "none" }}>
                    <GradeAssuranceBadge grade={gaCert.grade} gradeLabel={gaCert.grade_label} />
                  </Link>
                </div>
              )}
              <div className="mt-1 text-sm text-muted-foreground">{car.year} · {formatKm(car.mileage)} · {fuelLabel(car.fuel, lang)}{car.location ? ` · ${car.location}` : ""}</div>
              {(() => {
                const va = car.verified_at;
                if (!va) return null;
                const d = new Date(va);
                if (isNaN(d)) return null;
                const days = Math.floor((Date.now() - d.getTime()) / 86400000);
                if (days > 14) return null;
                const ago = days <= 0 ? "aujourd'hui" : days === 1 ? "hier" : `il y a ${days} j`;
                return (
                  <div data-testid="detail-freshness" className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-sky-500/30 bg-sky-500/[0.08] px-2.5 py-1 text-[11px] font-medium text-sky-300">
                    <CheckCircle2 size={12} /> Disponibilité vérifiée {ago}
                  </div>
                );
              })()}
              {car.source_brand && (
                <div data-testid="detail-source-brand" className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/[0.08] px-2.5 py-1 text-[11px] font-medium text-emerald-300">
                  <ShieldCheck size={12} /> {car.source_brand}
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-baseline gap-2">
                <span className="font-display text-4xl font-extrabold gradient-brand-text">{formatPriceAsk(car.price)}</span>
                {gaCert?.certified && <PriceJustificationTag />}
                {financeFrom > 0 && (
                  <button data-testid="detail-finance-from" onClick={() => setFinancingOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#C9D2DC]/30 bg-[#C9D2DC]/[0.08] px-2.5 py-1.5 text-sm font-bold text-[#C9D2DC] transition hover:bg-[#C9D2DC]/15">
                    <Landmark size={15} /> à partir de <span className="font-display text-base font-extrabold">{financeFrom.toLocaleString("fr-BE")} €</span>/mois
                  </button>
                )}
                {carMeta && carMeta.vat_deductible && Number(car.price) > 0 && (
                  <span data-testid="detail-vat-badge" className="inline-flex items-center gap-1 rounded-md bg-pactly-blue/15 px-2 py-1 text-xs font-bold text-pactly-blue">
                    <Receipt size={12} /> {t("d.vatDeductible")} · {t("d.excl")} {formatPrice(Math.round(car.price / 1.21))}
                  </span>
                )}
              </div>
              {Number(car.price) > 0 && (
                <span className="mt-1 inline-block text-sm font-semibold text-muted-foreground">{car.negotiable ? t("d.negotiable") : t("d.cashPrice")}</span>
              )}

              <div className="mt-1"><LiveViewers carId={car.id} popularity={Number(car.views || carMeta?.views || 0)} /></div>

              {isNewVehicle && (
                <div data-testid="detail-new-vehicle" className="mt-4 rounded-2xl border border-[#7FB2E8]/30 bg-[#3b82f6]/[0.07] p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-[#1E5288] to-[#3b82f6] px-2.5 py-1 text-xs font-extrabold uppercase tracking-wide text-white">
                      <Sparkles size={13} /> Neuf · 0 km
                    </span>
                    {nvDiscount > 0 && (
                      <span className="text-sm text-muted-foreground">Prix catalogue <s>{formatPrice(nvListPrice)}</s> · <span className="font-bold text-[#3b82f6]">−{nvDiscount}%</span></span>
                    )}
                  </div>
                  {nvLease && (
                    <div className="mt-3 flex items-center gap-2 rounded-xl border border-[#7FB2E8]/30 bg-white/[0.04] px-3 py-2">
                      <Landmark size={16} className="text-[#3b82f6]" />
                      <span className="text-sm font-bold text-foreground">Leasing dès {Number(nvLease.monthly).toLocaleString("fr-BE")} €/mois</span>
                      <span className="text-xs text-muted-foreground">{nvLease.down ? `· ${formatPrice(nvLease.down)} d'apport ` : "· 0 € d'apport "}{nvLease.duration ? `· ${nvLease.duration} mois` : ""}{nvLease.label ? ` · ${nvLease.label}` : ""}</span>
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                    {car.delivery_time && <div><div className="text-xs text-muted-foreground">Livraison</div><div className="font-semibold text-foreground">{car.delivery_time}</div></div>}
                    {car.availability && <div><div className="text-xs text-muted-foreground">Disponibilité</div><div className="font-semibold text-foreground">{car.availability === "stock" ? "En stock" : "Sur commande"}</div></div>}
                    {car.warranty_months > 0 && <div><div className="text-xs text-muted-foreground">Garantie</div><div className="font-semibold text-foreground">{car.warranty_months} mois</div></div>}
                    {Array.isArray(car.colors) && car.colors.length > 0 && <div className="col-span-2"><div className="text-xs text-muted-foreground">Couleurs</div><div className="font-semibold text-foreground">{car.colors.join(", ")}</div></div>}
                  </div>
                </div>
              )}

              <MarketComparison carId={car.id} fallbackDeal={deal} t={t} />

              <DnaSeal car={car} />
              <RehabPanel car={car} />


              {sold && (
                <div data-testid="detail-sold-banner" className="mt-4 flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
                  <ShieldCheck size={20} className="mt-0.5 shrink-0 text-emerald-400" />
                  <div className="text-sm">
                    <div className="font-bold text-emerald-300">Ce véhicule est vendu</div>
                    <div className="text-emerald-200/75">
                      {soldUntil ? `Vendu sur une plateforme partenaire. Cette annonce restera visible jusqu'au ${fmtSoldDate(soldUntil)}, puis sera automatiquement retirée.` : "Vendu sur une plateforme partenaire. Cette annonce sera bientôt retirée."}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-5 flex gap-3">
                {sold ? (
                  <Button data-testid="detail-sold-btn" disabled className="h-12 flex-1 cursor-not-allowed bg-emerald-600/80 text-base font-bold text-white opacity-90">
                    <ShieldCheck size={18} className="mr-2" /> Véhicule vendu
                  </Button>
                ) : (
                  <Button data-testid="detail-contact-btn" onClick={() => setContactOpen(true)} className="h-12 flex-1 gradient-brand text-base font-bold text-white hover:opacity-90">
                    <MessageSquare size={18} className="mr-2" /> {t("d.contactSeller")}
                  </Button>
                )}
                <button data-testid="detail-fav-btn" onClick={onFav} className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 text-white transition hover:bg-white/5">
                  <Heart size={20} className={fav ? "fill-destructive text-destructive" : ""} />
                </button>
                <button data-testid="detail-compare-btn" onClick={() => toggleCompare(car.id)} className={`flex h-12 w-12 items-center justify-center rounded-xl border transition ${inCompare ? "gradient-brand border-transparent text-white" : "border-white/10 text-white hover:bg-white/5"}`}>
                  <GitCompare size={19} />
                </button>
                <ShareMenu car={car} />
              </div>
              {isOwner && !isPro && (
                <Link to={`/contrat-de-vente?car=${car.id}`} data-testid="detail-contract-btn"
                  className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10">
                  <FileText size={16} className="text-pactly-violet" /> {t("d.generateContract")}
                </Link>
              )}
              {!isOwner && !sold && (
                <div className="mt-3 space-y-2" data-testid="buyer-actions-block">
                  <button data-testid="buyer-compare-btn" onClick={() => toggleCompare(car.id)}
                    className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition ${inCompare
                      ? "border border-[#8FA3BC] bg-[#0F2540] text-white shadow-[0_0_0_1px_rgba(143,163,188,0.35)_inset]"
                      : "border border-[#2E4A6B] bg-gradient-to-b from-[#13304F] to-[#0F2540] text-[#E7EEF6] hover:from-[#16375c] hover:to-[#122a47]"}`}>
                    <GitCompare size={17} className="text-[#AEBCCD]" />
                    {inCompare ? "Retirer de la comparaison" : "Comparer avec une autre annonce"}
                  </button>
                  <div className={`grid gap-2 ${insurancePartners.length ? "grid-cols-2" : "grid-cols-1"}`}>
                    {financing ? (
                      <button data-testid="buyer-credit-btn" onClick={() => setFinancingOpen(true)}
                        className="relative flex items-center justify-center gap-1.5 rounded-xl border border-[#C9D2DC]/40 bg-gradient-to-b from-[#13304F] to-[#0F2540] py-3 text-xs font-bold text-white transition hover:from-[#16375c] hover:to-[#122a47]">
                        <Calculator size={14} className="text-[#C9D2DC]" /> Simuler un crédit
                      </button>
                    ) : (
                      <button data-testid="buyer-credit-btn" disabled title="Bientôt disponible"
                        className="flex cursor-not-allowed flex-col items-center justify-center gap-1 rounded-xl border border-white/10 bg-white/[0.03] py-2.5 text-xs font-bold text-white/55">
                        <span className="flex items-center gap-1.5"><Calculator size={14} className="text-[#8FA3BC]" /> Simuler un crédit</span>
                        <span className="rounded-full bg-[#8FA3BC]/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#C9D6E5] ring-1 ring-[#8FA3BC]/40">Bientôt</span>
                      </button>
                    )}
                    {insurancePartners.map((p, i) => (
                      <InsurancePartnerButton key={p.category} partner={p} lang={lang}
                        className={(insurancePartners.length % 2 === 0 && i === insurancePartners.length - 1) ? "col-span-2" : ""} />
                    ))}
                  </div>
                </div>
              )}
              {isTaxReady(car) ? (
                <>
                  <div data-testid="detail-tax-ready-badge" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-bold text-emerald-400 ring-1 ring-emerald-400/40">
                    <Landmark size={13} /> {t("d.taxReadyBadge")}
                  </div>
                  <TaxCalculator car={car} />
                </>
              ) : (
                <p data-testid="detail-tax-unavailable" className="mt-3 text-xs text-muted-foreground">
                  {t("d.taxUnavailable")}
                </p>
              )}
              {canManage && (
                <button data-testid="detail-edit-btn" onClick={() => setEditOpen(true)}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-sky-300/40 bg-sky-400/15 py-2.5 text-sm font-bold text-sky-100 transition hover:bg-sky-400/25">
                  <Pencil size={16} /> {isAdmin && !isOwner ? "Modifier (admin)" : t("d.editListing")}
                </button>
              )}
              {canManage && (
                <button data-testid="detail-delete-btn" onClick={deleteListing}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 py-2.5 text-sm font-bold text-red-400 transition hover:bg-red-500/20">
                  <Trash2 size={16} /> Supprimer l'annonce
                </button>
              )}
              {isOwner && (
                <div className="mt-2 rounded-xl border border-white/10 bg-white/[0.04] p-3" data-testid="showcase-flyer-box">
                  <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#C9D2DC]">
                    <Printer size={13} /> Flyer vitrine (QR annonce)
                  </div>
                  <button data-testid="showcase-flyer-open" onClick={() => setFlyerOpen(true)}
                    className="flex w-full items-center justify-center gap-2 rounded-lg gradient-brand py-2.5 text-sm font-bold text-white transition hover:opacity-90">
                    <Printer size={15} /> Créer le flyer
                  </button>
                  <div className="mt-1.5 text-[10px] leading-snug text-white/40">Aperçu, 9 langues, A5 ou Story — téléchargez ou partagez sur WhatsApp.</div>
                </div>
              )}
              {isAdmin && dupAlts.length > 0 && (
                <div data-testid="admin-cross-platform" className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
                  <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-500">
                    <Layers size={13} /> Admin · Aussi présent sur
                  </div>
                  <div className="space-y-1.5">
                    {dupAlts.map((d) => (
                      <div key={d.id} data-testid={`cross-platform-alt-${d.id}`} className="flex items-center justify-between gap-2 text-xs text-white/80">
                        <span className="flex items-center gap-1.5 font-semibold text-white">
                          <ExternalLink size={11} className="text-amber-400" /> {d.label}
                        </span>
                        <span className="truncate text-white/50">{d.ref || d.id}{d.model ? ` · ${d.model}` : ""}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-[10px] leading-snug text-white/40">
                    {dupAlts.length} doublon{dupAlts.length > 1 ? "s" : ""} masqué{dupAlts.length > 1 ? "s" : ""} du catalogue public (même véhicule) — visible uniquement par les admins.
                  </div>
                </div>
              )}
              <SocialPoster car={car} imageUrl={images[0]} images={images} dealer={dealer?.logo ? dealer : { ...(dealer || {}), logo: sellerLogo }} />
              <button data-testid="detail-vehicle-sheet-btn"
                onClick={() => generateVehicleSheet({ ...car, images }, window.location.href, { lang, region: effRegion, dealer: { name: dealer?.name || (sellerIsPlatform ? t("d.proSellerGeneric") : car.seller_name), location: dealer?.location || car.seller_location, phone: car.seller_phone } })}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-secondary py-2.5 text-sm font-bold text-foreground transition hover:bg-accent">
                <Printer size={16} /> {t("detail.vehicleSheet")}
              </button>
              <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Partager cette annonce</div>
                <ShareButtons testid="car-share" title={`${car.make} ${car.model} (${car.year || ""}) - ${formatPrice(car.price)}`} />
              </div>
            </div>

            {/* seller */}
            <div className="rounded-2xl card-surface p-6">
              <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-wide text-white/50">{t("d.seller")}</h3>
              {car.ghost ? (
                <div data-testid="detail-ghost-seller">
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300"><Users size={20} /></span>
                    <div className="min-w-0">
                      <span className="truncate font-display font-bold text-white">Vendeur particulier</span>
                      {car.location && <div className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={11} /> {car.location}</div>}
                    </div>
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-2.5 py-1 text-[11px] font-bold text-amber-300" data-testid="ghost-source-badge">
                    <Sparkles size={11} /> {car.source_badge || "Source : Stock Particulier Externe"}
                  </div>
                  {car.verified_at && (() => {
                    const h = Math.max(0, Math.round((Date.now() - new Date(car.verified_at).getTime()) / 3600000));
                    const when = h < 1 ? "moins d'une heure" : h < 24 ? `${h} h` : `${Math.round(h / 24)} j`;
                    return (
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-400/90" data-testid="ghost-verified-line">
                        <ShieldCheck size={13} /> {`Informations vérifiées par le robot Pactly il y a ${when}`}
                      </div>
                    );
                  })()}
                </div>
              ) : dealer && dealer.name ? (
                <Link to={`/seller/${dealer.dealer_id || dealer.slug || car.seller_id}`} className="flex items-center gap-3" data-testid="detail-display-seller">
                  {dealer.logo
                    ? <img src={dealer.logo} alt="" className="h-12 w-12 rounded-full bg-white object-contain p-1" />
                    : <span className="flex h-12 w-12 items-center justify-center rounded-full gradient-brand text-lg font-bold text-white">{dealer.name.charAt(0)}</span>}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate font-display font-bold text-white">{dealer.name}</span>
                      <span className="flex items-center gap-0.5 rounded bg-pactly-blue/20 px-1.5 py-0.5 text-[9px] font-extrabold text-pactly-blue"><Store size={9} /> PRO</span>
                    </div>
                    {dealer.location && <div className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={11} /> {dealer.location}</div>}
                  </div>
                </Link>
              ) : sellerIsPlatform ? (
                <div className="flex items-center gap-3" data-testid="detail-seller-neutral">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-[#C9D2DC]"><Store size={18} /></span>
                  <div className="min-w-0">
                    <span className="truncate font-display font-bold text-white">{t("d.proSellerGeneric")}</span>
                    {car.location && <div className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={11} /> {car.location}</div>}
                  </div>
                </div>
              ) : (
              <Link to={`/seller/${car.seller_id}`} className="flex items-center gap-3" data-testid="detail-display-seller">
                {sellerAvatar ? (
                  <img src={sellerAvatar} alt="" className="h-12 w-12 rounded-full object-cover" data-testid="seller-avatar" />
                ) : (
                  <span className="flex h-12 w-12 items-center justify-center rounded-full gradient-brand text-lg font-bold text-white">{(car.seller_name || "?").charAt(0)}</span>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-display font-bold text-white">{sellerName || car.seller_name}</span>
                    {car.seller_verified && <ShieldCheck size={15} className="text-emerald-400" />}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                    {car.seller_merchant_verified && <span className="flex items-center gap-0.5 font-semibold text-pactly-blue"><Store size={11} /> PRO</span>}
                    {car.seller_reliability != null && <span>· {t("d.reliability")}: {car.seller_reliability}/100</span>}
                    {car.review_count > 0 && <span className="flex items-center gap-0.5">· <Star size={11} className="fill-amber-400 text-amber-400" /> {car.review_avg?.toFixed(1)} ({car.review_count})</span>}
                  </div>
                </div>
              </Link>
              )}
              {/* Premium showroom CTA for professional sellers */}
              {(dealer?.name || car.seller_merchant_verified) && (
                <div className="mt-3">
                  {sellerCars.length > 0 && (
                    <div className="mb-2" data-testid="detail-showroom-preview">
                      <div className="mb-1.5 text-xs font-semibold text-muted-foreground">{t("d.moreFromSeller")}</div>
                      <div className="grid grid-cols-3 gap-2">
                        {sellerCars.map((sc) => (
                          <Link key={sc.id} to={`/car/${sc.id}`} data-testid={`showroom-preview-${sc.id}`}
                            className="group relative overflow-hidden rounded-xl ring-1 ring-white/10">
                            <div className="aspect-[4/3] w-full overflow-hidden bg-white/5">
                              {(sc.images && sc.images[0])
                                ? <img src={thumbUrl(sc.images[0], 300)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                : <div className="flex h-full items-center justify-center text-white/30"><Store size={18} /></div>}
                            </div>
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 pb-1 pt-4">
                              <div className="truncate text-[10px] font-bold text-white">{sc.make} {sc.model}</div>
                              <div className="text-[10px] font-extrabold gradient-brand-text">{formatPrice(sc.price)}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  <Link
                    to={`/seller/${dealer?.dealer_id || dealer?.slug || car.seller_id}`}
                    data-testid="detail-showroom-cta"
                    className="gradient-brand on-dark cta-glow group relative flex items-center gap-3 overflow-hidden rounded-2xl px-4 py-3 text-white"
                  >
                    <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
                      <span className="absolute -inset-y-6 -left-1/3 w-1/3 -skew-x-12 bg-white/25 opacity-0 transition-all duration-700 group-hover:left-[130%] group-hover:opacity-100" />
                    </span>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20 ring-1 ring-white/30 backdrop-blur">
                      <Store size={18} />
                    </span>
                    <span className="relative min-w-0 flex-1">
                      <span className="block truncate font-display text-sm font-extrabold">{t("seller.viewShowroom")}</span>
                      <span className="block truncate text-xs text-white/80">{t("seller.viewShowroomSub")}</span>
                    </span>
                    <ArrowRight size={18} className="relative shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* similar */}
      {similar.length > 0 && (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-white">{t("d.similar")}</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {similar.map((c) => <CarCard key={c.id} car={c} />)}
          </div>
        </section>
      )}

      <SeoInternalLinks car={car} />

      {/* contact dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="border-white/10 bg-pactly-ink2 text-white">
          <DialogHeader><DialogTitle className="font-display">{t("d.contactSeller")}{dealer?.name ? ` · ${dealer.name}` : sellerIsPlatform ? "" : car.seller_name ? ` · ${car.seller_name}` : ""}</DialogTitle></DialogHeader>
          {!user ? (
            <div className="py-4 text-center">
              <p className="text-muted-foreground">{t("msg.loginNeeded")}</p>
              <Link to="/login"><Button className="mt-4 gradient-brand text-white">{t("nav.login")}</Button></Link>
            </div>
          ) : (
            <div className="space-y-4">
              <Textarea data-testid="contact-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={4}
                className="border-white/10 bg-white/5 text-white" />
              <Button data-testid="contact-send-btn" onClick={sendMessage} disabled={sending} className="w-full gradient-brand font-bold text-white">
                {sending ? "…" : t("common.send")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Lightbox images={images} index={idx} setIndex={setIdx} open={lightboxOpen} onClose={() => setLightboxOpen(false)} alt={title} />
      {editOpen && carRaw && (
        <ListingEditModal car={{ ...carRaw, images: (car.images || []) }} onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); refreshListing(); }} />
      )}
      {financingOpen && financing && (
        <FinancingSimulatorDialog car={car} sellerId={sellerKey} config={financing} onClose={() => setFinancingOpen(false)} />
      )}
      {flyerOpen && <FlyerStudioDialog carId={car.id} car={car} open={flyerOpen} onClose={() => setFlyerOpen(false)} />}
    </div>
  );
}

function NavBtn({ side, onClick }) {
  const Icon = side === "left" ? ChevronLeft : ChevronRight;
  return (
    <button onClick={onClick} data-testid={`gallery-${side}`}
      className={`absolute top-1/2 -translate-y-1/2 ${side === "left" ? "left-3" : "right-3"} flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur transition hover:bg-black/70`}>
      <Icon size={22} />
    </button>
  );
}
